import {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

const UsersServiceContext = createContext(undefined)

const UsersServiceProvider = ({
  children,
  oauthApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}) => {
  return (
    <ResourceApiServiceProvider
      Context={UsersServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={oauthApiHost}
      valueCallback={(axios) => ({
        create: async (payload) => {
          const {data} = await axios.post(`/users`, payload)
          return data
        },
        getMe: async (authorization) => {
          const {data} = await axios.get(`/users/me`, {
            headers: {
              Authorization: `Bearer ${authorization}`
            }
          })
          return data
        },
        get: async (id) => {
          const {data} = await axios.get(`/users/${id}`)
          return data
        },
        list: async (query) => {
          const qs2 = qs.stringify(query, {
            addQueryPrefix: true
          })
          const {data} = await axios.get(`/users${qs2}`)
          return data
        },
        update: async (id, payload) => {
          const {data} = await axios.patch(`/users/${id}`, payload)
          return data
        },
        sendVerificationEmail: async ({authorization, payload}) => {
          const {data} = await axios.post(`/email_verification_tokens`, payload, {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authorization}`
            }
          })
          return data
        },
        slackUserSyncUser: async (id) => {
          const {data} = await axios.post(`/users/${id}/slack_user_sync`)
          return data
        },
        listLoginHistories: async (id, query) => {
          const {data} = await axios.get(
            `/users/${id}/history${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        reset2FA: async (id) => {
          const {data} = await axios.post(`/users/${id}/reset_2fa`)
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

UsersServiceProvider.propTypes = {
  children: PropTypes.any,
  oauthApiHost: PropTypes.string.isRequired,
  handleUnauthorized: PropTypes.func
}

const useUsersService = () => {
  const context = useContext(UsersServiceContext)
  if (context === undefined) {
    throw new Error('UsersServiceContext was not provided. Make sure your component is a child of the UsersServiceProvider.')
  }
  return context
}

const useCurrentUserQuery = ({accessToken, enabled, ...otherProps}) => {
  const usersService = useUsersService()
  return useSimpleQuery({
    queryKey: `users-me-${accessToken}`,
    queryFn: async () => usersService.getMe(accessToken),
    enabled: enabled !== undefined ? enabled : !!accessToken,
    ...otherProps
  })
}

const useUserQuery = ({id, enabled, ...otherProps}) => {
  const usersService = useUsersService()
  return useSimpleQuery({
    queryKey: `users-get-${id}`,
    queryFn: async () => usersService.get(id),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

const useUsersQuery = ({query, ...otherProps}) => {
  const usersService = useUsersService()
  return useSimpleQuery({
    queryKey: `users-list-${JSON.stringify(query)}`,
    queryFn: async () => usersService.list(query),
    ...otherProps
  })
}

const useUserLoginHistoriesQuery = ({id, query, enabled, ...otherProps}) => {
  const usersService = useUsersService()
  return useSimpleQuery({
    queryKey: `user_login_histories-list-${JSON.stringify(query)}`,
    queryFn: async () => usersService.listLoginHistories(id, query),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

export {UsersServiceProvider, useUsersService, useCurrentUserQuery, useUserQuery, useUsersQuery, useUserLoginHistoriesQuery}
